import React, { useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';

type FormType = 'creator' | 'brand';
type FollowerCount = '0-100k' | '100-250k' | '250k-1m' | '1-5m' | '+';
type Budget = '$0-$10k' | '$10-$50k' | '$50-$250k' | '$250k-$1m' | '+';

interface CreatorFormData {
  name: string;
  links: {
    youtube: string;
    tiktok: string;
    instagram: string;
    twitch: string;
  };
  management: string;
  email: string;
  source: string;
  followers: FollowerCount;
  message: string;
}

interface BrandFormData {
  name: string;
  company: string;
  email: string;
  creator: string;
  source: string;
  message: string;
  budget: Budget;
}

const ContactForm: React.FC = () => {
  const [formType, setFormType] = useState<FormType>('brand');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  
  const [creatorData, setCreatorData] = useState<CreatorFormData>({
    name: '',
    links: {
      youtube: '',
      tiktok: '',
      instagram: '',
      twitch: ''
    },
    management: '',
    email: '',
    source: '',
    followers: '0-100k',
    message: ''
  });
  
  const [brandData, setBrandData] = useState<BrandFormData>({
    name: '',
    company: '',
    email: '',
    creator: '',
    source: '',
    message: '',
    budget: '$0-$10k'
  });

  useEffect(() => {
    // Initialize EmailJS with your public key
    emailjs.init('3Q_YRDeDu66_KVMvR');
  }, []);

  const handleCreatorChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      if (parent === 'links') {
        setCreatorData(prev => ({
          ...prev,
          links: {
            ...prev.links,
            [child]: value
          }
        }));
      }
    } else {
      setCreatorData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleBrandChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setBrandData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate creator form
    if (formType === 'creator') {
      const hasAnySocialLink = Object.values(creatorData.links).some(link => link.trim() !== '');
      if (!hasAnySocialLink) {
        setStatus('error');
        setErrorMessage('Please provide at least one social media link');
        return;
      }
    }

    setStatus('loading');
    setErrorMessage('');

    try {
      const templateParams = formType === 'creator' 
        ? {
            name: creatorData.name,
            email: creatorData.email,
            time: new Date().toLocaleString(),
            message: `
              Type: Creator
              Name: ${creatorData.name}
              YouTube: ${creatorData.links.youtube}
              TikTok: ${creatorData.links.tiktok}
              Instagram: ${creatorData.links.instagram}
              Twitch: ${creatorData.links.twitch}
              Management: ${creatorData.management}
              Source: ${creatorData.source}
              Followers: ${creatorData.followers}
              Message: ${creatorData.message}
            `
          }
        : {
            name: brandData.name,
            email: brandData.email,
            time: new Date().toLocaleString(),
            message: `
              Type: Brand
              Name: ${brandData.name}
              Company: ${brandData.company}
              Creator Interest: ${brandData.creator}
              Source: ${brandData.source}
              Budget: ${brandData.budget}
              Message: ${brandData.message}
            `
          };

      await emailjs.send(
        'service_d9d38si',
        'template_vzcw8lw',
        templateParams
      );

      setStatus('success');
      
      if (formType === 'creator') {
        setCreatorData({
          name: '',
          links: { youtube: '', tiktok: '', instagram: '', twitch: '' },
          management: '',
          email: '',
          source: '',
          followers: '0-100k',
          message: ''
        });
      } else {
        setBrandData({
          name: '',
          company: '',
          email: '',
          creator: '',
          source: '',
          message: '',
          budget: '$0-$10k'
        });
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage('Failed to send message. Please try again later.');
      console.error('EmailJS error:', error);
    }
  };

  const radioStyle = "relative flex items-center justify-center h-14 w-full border-2 border-[#2D2F45] rounded-lg cursor-pointer transition-all hover:border-[#6927DA]";
  const activeRadioStyle = "border-[#6927DA] bg-[#6927DA]/10";
  const inputStyle = "w-full px-4 py-3 bg-[#1A1B23] border border-[#2D2F45] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-[#6927DA] transition-colors duration-300";
  const labelStyle = "block text-sm font-medium text-gray-300 mb-2 text-left";
  const sectionStyle = "mb-6";

  const socialIconStyle = "w-5 h-5 mr-2";
  const socialInputStyle = "flex items-center px-4 py-3 bg-[#1A1B23] border border-[#2D2F45] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-[#6927DA] transition-colors duration-300";
  const requiredStyle = "text-[#6927DA] ml-1";

  return (
    <div className="p-6 bg-[#0F1116] border border-[#2D2F45] rounded-xl space-y-8">
      {/* Form Type Selector */}
      <div className="grid grid-cols-2 gap-4">
        <div 
          className={`${radioStyle} ${formType === 'creator' ? activeRadioStyle : ''}`}
          onClick={() => setFormType('creator')}
        >
          <div className="text-center">
            <div className="font-medium text-white">Influencer?</div>
            <div className="text-sm text-gray-400">I'm a creator</div>
          </div>
        </div>
        <div 
          className={`${radioStyle} ${formType === 'brand' ? activeRadioStyle : ''}`}
          onClick={() => setFormType('brand')}
        >
          <div className="text-center">
            <div className="font-medium text-white">Product?</div>
            <div className="text-sm text-gray-400">I'm a brand</div>
          </div>
        </div>
      </div>

      <h3 className="text-2xl font-serif text-white">Let's talk 👋</h3>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {formType === 'creator' ? (
          /* Creator Form */
          <>
            <div className={sectionStyle}>
              <label htmlFor="name" className={labelStyle}>Name or creator name<span className={requiredStyle}>*</span></label>
              <input
                type="text"
                id="name"
                name="name"
                value={creatorData.name}
                onChange={handleCreatorChange}
                required
                className={inputStyle}
                placeholder="Tony Stark"
              />
            </div>

            <div className={sectionStyle}>
              <label className={labelStyle}>Links<span className={requiredStyle}>*</span></label>
              <div className="space-y-4">
                <div className={socialInputStyle}>
                  <svg className={socialIconStyle} viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                  <input
                    type="text"
                    name="links.youtube"
                    value={creatorData.links.youtube}
                    onChange={handleCreatorChange}
                    className="flex-1 bg-transparent border-none focus:outline-none"
                    placeholder="YouTube link"
                  />
                </div>
                <div className={socialInputStyle}>
                  <svg className={socialIconStyle} viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                  </svg>
                  <input
                    type="text"
                    name="links.tiktok"
                    value={creatorData.links.tiktok}
                    onChange={handleCreatorChange}
                    className="flex-1 bg-transparent border-none focus:outline-none"
                    placeholder="TikTok link"
                  />
                </div>
                <div className={socialInputStyle}>
                  <svg className={socialIconStyle} viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.012-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                  <input
                    type="text"
                    name="links.instagram"
                    value={creatorData.links.instagram}
                    onChange={handleCreatorChange}
                    className="flex-1 bg-transparent border-none focus:outline-none"
                    placeholder="Instagram link"
                  />
                </div>
                <div className={socialInputStyle}>
                  <svg className={socialIconStyle} viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3l-3 3v-3H6.857V1.714h13.714Z"/>
                  </svg>
                  <input
                    type="text"
                    name="links.twitch"
                    value={creatorData.links.twitch}
                    onChange={handleCreatorChange}
                    className="flex-1 bg-transparent border-none focus:outline-none"
                    placeholder="Twitch link"
                  />
                </div>
              </div>
            </div>

            <div className={sectionStyle}>
              <label htmlFor="management" className={labelStyle}>Management company</label>
              <input
                type="text"
                id="management"
                name="management"
                value={creatorData.management}
                onChange={handleCreatorChange}
                className={inputStyle}
                placeholder="Echo Network"
              />
            </div>

            <div className={sectionStyle}>
              <label htmlFor="email" className={labelStyle}>Email<span className={requiredStyle}>*</span></label>
              <input
                type="email"
                id="email"
                name="email"
                value={creatorData.email}
                onChange={handleCreatorChange}
                required
                className={inputStyle}
                placeholder="name@echo-network.pp.ua"
              />
            </div>

            <div className={sectionStyle}>
              <label htmlFor="source" className={labelStyle}>Where did you hear about us?</label>
              <input
                type="text"
                id="source"
                name="source"
                value={creatorData.source}
                onChange={handleCreatorChange}
                className={inputStyle}
                placeholder="Search engine, friend, Instagram, creator's email, etc."
              />
            </div>

            <div className={sectionStyle}>
              <label className={labelStyle}>Number of followers<span className={requiredStyle}>*</span></label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {(['0-100k', '100-250k', '250k-1m', '1-5m', '+'] as FollowerCount[]).map(option => (
                  <div 
                    key={option}
                    className={`${radioStyle} ${creatorData.followers === option ? activeRadioStyle : ''} h-10`}
                    onClick={() => setCreatorData(prev => ({ ...prev, followers: option }))}
                  >
                    <div className="text-center text-sm text-white">{option}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className={sectionStyle}>
              <label htmlFor="message" className={labelStyle}>Message<span className={requiredStyle}>*</span></label>
              <textarea
                id="message"
                name="message"
                value={creatorData.message}
                onChange={handleCreatorChange}
                required
                rows={4}
                className={inputStyle}
                placeholder="Compose your message..."
              />
            </div>
          </>
        ) : (
          /* Brand Form */
          <>
            <div className={sectionStyle}>
              <label htmlFor="name" className={labelStyle}>Name<span className={requiredStyle}>*</span></label>
              <input
                type="text"
                id="name"
                name="name"
                value={brandData.name}
                onChange={handleBrandChange}
                required
                className={inputStyle}
                placeholder="Tony Stark"
              />
            </div>

            <div className={sectionStyle}>
              <label htmlFor="company" className={labelStyle}>Company<span className={requiredStyle}>*</span></label>
              <input
                type="text"
                id="company"
                name="company"
                value={brandData.company}
                onChange={handleBrandChange}
                required
                className={inputStyle}
                placeholder="Stark Industries"
              />
            </div>

            <div className={sectionStyle}>
              <label htmlFor="email" className={labelStyle}>Email<span className={requiredStyle}>*</span></label>
              <input
                type="email"
                id="email"
                name="email"
                value={brandData.email}
                onChange={handleBrandChange}
                required
                className={inputStyle}
                placeholder="name@echo-network.pp.ua"
              />
            </div>

            <div className={sectionStyle}>
              <label htmlFor="creator" className={labelStyle}>Creator you'd like to work with (if any)</label>
              <input
                type="text"
                id="creator"
                name="creator"
                value={brandData.creator}
                onChange={handleBrandChange}
                className={inputStyle}
                placeholder="Creator's name"
              />
            </div>

            <div className={sectionStyle}>
              <label htmlFor="source" className={labelStyle}>Where did you hear about us?</label>
              <input
                type="text"
                id="source"
                name="source"
                value={brandData.source}
                onChange={handleBrandChange}
                className={inputStyle}
                placeholder="Search engine, LinkedIn, Instagram, friend, etc."
              />
            </div>

            <div className={sectionStyle}>
              <label htmlFor="message" className={labelStyle}>Message<span className={requiredStyle}>*</span></label>
              <textarea
                id="message"
                name="message"
                value={brandData.message}
                onChange={handleBrandChange}
                required
                rows={4}
                className={inputStyle}
                placeholder="Compose your message..."
              />
            </div>

            <div className={sectionStyle}>
              <label className={labelStyle}>Budget<span className={requiredStyle}>*</span></label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {(['$0-$10k', '$10-$50k', '$50-$250k', '$250k-$1m', '+'] as Budget[]).map(option => (
                  <div 
                    key={option}
                    className={`${radioStyle} ${brandData.budget === option ? activeRadioStyle : ''} h-10`}
                    onClick={() => setBrandData(prev => ({ ...prev, budget: option }))}
                  >
                    <div className="text-center text-sm text-white">{option}</div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        <div>
          <button
            type="submit"
            disabled={status === 'loading'}
            className="w-full px-8 py-4 text-white bg-[#6927DA] hover:bg-[#5925DC] transition-all duration-300 rounded-full text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {status === 'loading' ? 'Sending...' : 'Send Message'}
          </button>
        </div>

        {status === 'success' && (
          <div className="p-4 bg-green-900/20 border border-green-800 rounded-lg text-green-400 text-center">
            Thank you for your message! We'll get back to you soon.
          </div>
        )}

        {status === 'error' && (
          <div className="p-4 bg-red-900/20 border border-red-800 rounded-lg text-red-400 text-center">
            {errorMessage}
          </div>
        )}
      </form>
    </div>
  );
};

export default ContactForm; 